import type { AutomaticProcessingCodeType } from '@/components/report-forms/constants/automaticProcessingCodeType';
import type { JsonFormHistory } from './jsonForm';

export type EoFormWithTypeAndHistoryBase = { type: 'eoForm'; hierarchyRootLevel: boolean; formHistory: JsonFormHistory[]; calculation: string | null; formula: string };
export type EoFormWithTypeAndHistory = EoFormWithTypeAndHistoryBase & EoForm;
export type EoForm = EoForm2 | EoForm3 | EoForm4 | EoForm5 | EoForm6 | EoForm10;
export type EoFormImportFormPosition = EoForm2ImportForm | EoForm3ImportForm | EoForm4ImportForm | EoForm5ImportForm | EoForm6ImportForm | EoForm10ImportForm;
export type EoFormSubTableData = EoFormSubTableDataThreeOrSixOrTen | never;
export type EoFormSubTableDataWithParentId = EoFormSubTableData & { parentId: number };
export type EoFormSubTableDataSixOrTen = EoForm6SubTableData | EoForm10SubTableData;
export type EoFormSubTableDataThreeOrSixOrTen = EoForm3SubTableData | EoForm6SubTableData | EoForm10SubTableData;
export type EoFormWithOrdinalNo = EoForm2 | EoForm3 | EoForm4 | EoForm6 | EoForm10;

enum FormTypes {
  EmployeeExpenses = 10,
  EmployeeDynamics = 11,
  Subventions = 12,
  ServiceAndProductPrices = 13,
  SpecialUses = 14,
  InvestmentReport = 15
}

export type EoForm2 = {
  automaticProcessingCodeType: AutomaticProcessingCodeType;
  id: number;
  level: number;
  ordinalNo: string;
  planCurrentPeriod: number | null;
  planCurrentYear: number | null;
  position: string;
  realizationCurrentPeriod: number | null;
  realizationPercentage: number | null;
  realizationPreviousYear: number | null;
  subTableData: never[];
  children: EoForm2[] | null;
  parentId: (number | null)[];
  hierarchyRootLevel: boolean;
};

export type EoForm3 = {
  automaticProcessingCodeType: AutomaticProcessingCodeType;
  calculationType: string;
  contractEmployeesNumber: number | null;
  id: number;
  level: number;
  nestedTable: boolean;
  ordinalNo: string;
  parentId: (number | null)[];
  permanentEmployeesNumber: number | null;
  position: string;
  subTableData: EoForm3SubTableData[] | never[];
  temporaryEmployeesNumber: number | null;
  children: EoForm3[] | null;
  hierarchyRootLevel: boolean;
};

export type EoForm3SubTableData = {
  parentId: 15;
  position: string;
  totalPermanentEmployeesNumber: number | null;
  totalPermanentEmployeesNumberMen: number | null;
  totalPermanentEmployeesNumberWomen: number | null;
  totalTemporaryEmployeesNumber: number | null;
  totalTemporaryEmployeesNumberMen: number | null;
  totalTemporaryEmployeesNumberWomen: number | null;
  totalContractEmployeesNumber: number | null;
  totalContractEmployeesNumberMen: number | null;
  totalContractEmployeesNumberWomen: number | null;
};

export type EoForm4 = {
  automaticProcessingCodeType: AutomaticProcessingCodeType;
  budgetRegion: string;
  id: number | null;
  ordinalNo: string;
  planCurrentYear: number | null;
  position: string;
  priceIndex: number | null;
  realizationCurrentPeriod: number | null;
  transferFromBudget: number | null;
  unspentFunds: number | null;
  unspentFundsPreviousYears: number | null;
  subTableData: never[];
  children: EoForm4[] | null;
  parentId: (number | null)[];
  hierarchyRootLevel: boolean;
};

export type EoForm5 = {
  automaticProcessingCodeType: AutomaticProcessingCodeType;
  firstMonthCurrentPeriod: number;
  id: number | null;
  modifiedDate: string;
  modifiedUserId: number;
  position: string;
  previousYearLastMonth: number;
  priceIndex: number;
  secondMonthCurrentPeriod: number;
  thirdMonthCurrentPeriod: number;
  unspentFunds: number | null;
  subTableData: never[];
  children: EoForm5[] | null;
  parentId: (number | null)[];
  hierarchyRootLevel: boolean;
};

export type EoForm6 = {
  automaticProcessingCodeType: AutomaticProcessingCodeType;
  id: number;
  level: number;
  nestedTable: boolean;
  ordinalNo: string;
  planCurrentPeriod: number | null;
  planCurrentYear: number | null;
  planPreviousYear: number | null;
  position: string;
  realizationCurrentPeriod: number | null;
  realizationPercentage: number | null;
  realizationPreviousYear: number | null;
  subTableData: EoForm6SubTableData[];
  children: EoForm6[] | null;
  parentId: (number | null)[];
  hierarchyRootLevel: boolean;
};

export type EoForm6SubTableData = {
  id: number;
  amount: number;
  purpose: string;
  recipient: string;
  parentId: number;
};

export type EoForm10 = {
  automaticProcessingCodeType: AutomaticProcessingCodeType;
  id: number | null;
  level: number;
  nestedTable: boolean;
  ordinalNo: string | number | null;
  position: string;
  projectFundingEndYear: string;
  projectFundingStartYear: string;
  realizationPreviousYear: number;
  subTableData: EoForm10SubTableData[];
  totalProjectValue: number;
  children: EoForm10[] | null;
  parentId: (number | null)[];
  hierarchyRootLevel: boolean;
};

export type EoForm10SubTableData = {
  id: number;
  planCurrentPeriod: number | null;
  position: string;
  realizationCurrentPeriod: number | null;
};

export type EoForm2ImportForm = {
  type: FormTypes.EmployeeExpenses;
  positions: EoForm2[];
};

export type EoForm3ImportForm = {
  type: FormTypes.EmployeeDynamics;
  positions: EoForm3[];
};

export type EoForm4ImportForm = {
  type: FormTypes.Subventions;
  positions: EoForm4[];
};

export type EoForm5ImportForm = {
  type: FormTypes.ServiceAndProductPrices;
  positions: EoForm5[];
};

export type EoForm6ImportForm = {
  type: FormTypes.SpecialUses;
  positions: EoForm6[];
};

export type EoForm10ImportForm = {
  type: FormTypes.InvestmentReport;
  positions: EoForm10[];
};

export function isEoFormSubTableDataSixOrTen(subTableData: EoFormSubTableData): subTableData is EoFormSubTableDataSixOrTen {
  return 'id' in subTableData;
}

export type EoFormForRemoval = EoFormRowForRemoval | EoFormSubRowForRemoval | { row: null; isChildRow: false };

export type EoFormRowForRemoval = {
  isChildRow: false;
  row: EoForm;
};

export type EoFormSubRowForRemoval = {
  isChildRow: true;
  row: EoForm6SubTableData;
};

export function isEoForm3TotalRow(row: EoForm): row is EoForm3 {
  return 'nestedTable' in row && row.nestedTable && 'subTableData' in row && row.subTableData?.length === 1 && row.id === 15;
}

export function isEoForm3SubTableData(subTableData: EoFormSubTableData): subTableData is EoForm3SubTableData {
  return 'parentId' in subTableData
    && subTableData.parentId === 15
    && 'totalPermanentEmployeesNumber' in subTableData
    && 'totalPermanentEmployeesNumberMen' in subTableData
    && 'totalPermanentEmployeesNumberWomen' in subTableData
    && 'totalTemporaryEmployeesNumber' in subTableData
    && 'totalTemporaryEmployeesNumberMen' in subTableData
    && 'totalTemporaryEmployeesNumberWomen' in subTableData
    && 'totalContractEmployeesNumber' in subTableData
    && 'totalContractEmployeesNumberMen' in subTableData
    && 'totalContractEmployeesNumberWomen' in subTableData;
}

export function isEoForm10SubTableData(subTableData: EoFormSubTableData): subTableData is EoForm10SubTableData {
  return 'position' in subTableData && 'id' in subTableData && 'planCurrentPeriod' in subTableData && 'realizationCurrentPeriod' in subTableData;
}

export function isEoForm6SubTableData(subTableData: EoFormSubTableData): subTableData is EoForm6SubTableData {
  return 'purpose' in subTableData && 'recipient' in subTableData && 'id' in subTableData && 'amount' in subTableData;
}

export function isEoFormWithOrdinalNo(form: EoForm | EoFormSubTableData | EoFormWithTypeAndHistory): form is EoFormWithOrdinalNo {
  return 'ordinalNo' in form;
}
